import type {
  InstitutionSettings,
  ConfigurableMeetingMethod,
} from "@/lib/api/settings";
import type { ConflictFact } from "@/components/ui/conflict-notice";

/** A8 words, CONTRACT v2.1. Providers keep ownership of their own records. */
export const settingsCopy = {
  title: "Institution Settings",
  description:
    "Course delivery, meeting rooms and the grade scale for your institution.",
  delivery: "Course delivery",
  rooms: "Managed meeting rooms",
  history: "Change history",
  scale: "Grade scale",
  profile: "My Profile",
  receiverWillBeReplaced: "The current administrator will be replaced.",
  record: "My record",
  contact: "Contact details",
  unavailable: "Couldn't read this information.",
  stale: "Couldn't refresh. A later change isn't shown.",
  deliveryUnavailable: "Couldn't read institution settings. Nothing below is a setting until it reads.",
  roomsUnavailable: "Couldn't read managed rooms. Comes back with course delivery.",
  historyUnavailable: "Couldn't read recent saves. Comes back with course delivery.",
  scaleUnavailable: "Couldn't read the grade scale.",
  recordUnavailable: "Couldn't read your academic record.",
  contactUnavailable: "Couldn't read contact details. Comes back with My record.",
  eventUnavailable: "Couldn't read recording delivery from Zoom.",
  receiverUnavailable: "Couldn't read who receives recordings. Comes back with Zoom event processing.",
  contactMissing: "Contact details appear when the registrar creates your student record.",
  forbidden: "This page is not available to you.",
  unknown: "Check asks IMAM what happened. It never sends the save again.",
  lastMethod: "Keep at least one meeting method on.",
  METHODS: ["zoom", "google_meet", "in_person", "other"] as const,
  method: {
    zoom: "Zoom",
    google_meet: "Google Meet",
    in_person: "In person",
    other: "Other",
  },
};

const METHODS = settingsCopy.METHODS;
const LABEL = settingsCopy.method;

export type SettingsChange = InstitutionSettings["recent_changes"][number];

/** Course delivery as the administrator states it: what a save sends. */
export interface DeliveryEntry {
  enabled: ConfigurableMeetingMethod[];
  defaultMethod: ConfigurableMeetingMethod;
  locations: string[];
}

export function deliveryOf(settings: InstitutionSettings): DeliveryEntry {
  return {
    enabled: settings.course_delivery.enabled_meeting_methods,
    defaultMethod: settings.course_delivery.default_meeting_method,
    locations: settings.course_delivery.locations,
  };
}

function sameList<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/** Enabled methods are a set; the switches read them in one order. */
function sameMethods(
  a: ConfigurableMeetingMethod[],
  b: ConfigurableMeetingMethod[],
): boolean {
  return sameList(
    METHODS.filter((method) => a.includes(method)),
    METHODS.filter((method) => b.includes(method)),
  );
}

export function sameDelivery(a: DeliveryEntry, b: DeliveryEntry): boolean {
  return (
    a.defaultMethod === b.defaultMethod &&
    sameMethods(a.enabled, b.enabled) &&
    sameList(a.locations, b.locations)
  );
}

/** Their saved course delivery, fact by fact, where it differs from the entry here. */
export function deliveryConflictFacts(
  theirs: DeliveryEntry,
  mine: DeliveryEntry,
): ConflictFact[] {
  const facts: ConflictFact[] = [];
  if (!sameMethods(theirs.enabled, mine.enabled)) {
    facts.push({
      label: "Meeting methods",
      theirs: METHODS.filter((method) => theirs.enabled.includes(method))
        .map((method) => LABEL[method])
        .join(", "),
    });
  }
  if (theirs.defaultMethod !== mine.defaultMethod) {
    facts.push({
      label: "Default meeting method",
      theirs: LABEL[theirs.defaultMethod],
    });
  }
  if (!sameList(theirs.locations, mine.locations)) {
    facts.push({ label: "Locations", theirs: theirs.locations.join("\n") });
  }
  return facts;
}

/**
 * The history rows carry the exact four keys migration 073 writes into
 * `before_state` and `after_state`, so this narrows rather than guesses. An
 * unrecognised shape yields no lines and the entry falls back to saying only
 * that something changed, which is honest about a payload this build does not
 * understand instead of rendering `[object Object]`.
 */
function readState(state: Record<string, unknown>) {
  const methods = state.enabled_meeting_methods;
  const locations = state.locations;
  return {
    methods: Array.isArray(methods)
      ? methods.filter((v): v is string => typeof v === "string")
      : null,
    defaultMethod:
      typeof state.default_meeting_method === "string"
        ? state.default_meeting_method
        : null,
    locations: Array.isArray(locations)
      ? locations.filter((v): v is string => typeof v === "string")
      : null,
  };
}

function methodLabel(value: string): string {
  return LABEL[value as ConfigurableMeetingMethod] ?? value;
}

function listDiff(before: string[] | null, after: string[] | null) {
  if (!before || !after) return { added: [], removed: [] };
  return {
    added: after.filter((value) => !before.includes(value)),
    removed: before.filter((value) => !after.includes(value)),
  };
}

/**
 * A settings row is only useful as the difference it made, so the entry reads
 * out the fields that actually moved rather than dumping both states. A save
 * that changed nothing observable says so instead of rendering an empty entry.
 */
export function describeChange(entry: SettingsChange): {
  lines: string[];
  recognised: boolean;
} {
  const before = readState(entry.before_state);
  const after = readState(entry.after_state);
  const lines: string[] = [];
  // A shape this build cannot read is a different statement from a save that
  // moved nothing, and saying "no change" for both would assert something the
  // entry never established.
  const recognised = [before, after].every(
    (state) =>
      state.methods !== null ||
      state.defaultMethod !== null ||
      state.locations !== null,
  );

  const methods = listDiff(before.methods, after.methods);
  if (methods.added.length > 0) {
    lines.push(`Enabled ${methods.added.map(methodLabel).join(", ")}`);
  }
  if (methods.removed.length > 0) {
    lines.push(`Disabled ${methods.removed.map(methodLabel).join(", ")}`);
  }
  if (before.defaultMethod !== after.defaultMethod && after.defaultMethod) {
    lines.push(
      before.defaultMethod
        ? `Default meeting method ${methodLabel(before.defaultMethod)} to ${methodLabel(after.defaultMethod)}`
        : `Default meeting method set to ${methodLabel(after.defaultMethod)}`,
    );
  }
  const locations = listDiff(before.locations, after.locations);
  if (locations.added.length > 0) {
    lines.push(`Added ${locations.added.join(", ")}`);
  }
  if (locations.removed.length > 0) {
    lines.push(`Removed ${locations.removed.join(", ")}`);
  }
  return { lines, recognised };
}

export function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
}

/** Shared A8 labels and sentences. Dynamic copy below derives from typed data. */
export const settingsWords = {
  someoneSavedANewerVersionChooseWhichSettingsToKeep:
    "Someone saved a newer version. Choose which settings to keep.",
  settingsVersion: "Settings version",
  keepMine: "Keep mine",
  loadTheirs: "Load theirs",
  chooseWhichSettingsToKeep: "Choose which settings to keep.",
  refreshBeforeChoosingSettings: "Refresh the newer settings before choosing which to keep.",
  useAtMost50LocationsEachUpTo200Characters:
    "Use at most 50 locations, each up to 200 characters.",
  courseDeliverySavedNewCoursesUseTheseSettings:
    "Course delivery saved. New courses use these settings.",
  newestFirst: "Newest first",
  scaleRecorded: "Scale recorded",
  checkInstructorAgain: "Check the instructor's Zoom account again before refreshing.",
  changed: "Changed",
  asSaved: "As saved",
  chooseCourseDeliveryMethodsAndLocations:
    "Choose course delivery methods and locations",
  chooseMethodsStaffMaySelectAndTheDefaultForNewCourses:
    "Choose methods staff may select and the default for new courses.",
  changesNotSaved: "Changes not saved",
  courseDeliverySet: "Course delivery set",
  meetingMethods: "Meeting methods",
  coursesUseIt: "courses use it",
  defaultMeetingMethod: "Default meeting method",
  locations: "Locations",
  oneLocationPerLineBlankLinesAreIgnored:
    "One location per line. Blank lines are ignored.",
  reason: "Reason",
  optional: "Optional",
  reasonForThisChangeOptional: "Reason for this change, optional",
  keptInChangeHistoryWithThisSave: "Kept in Change history with this save.",
  saveCourseDelivery: "Save course delivery",
  whenYouSaveCourseDelivery: "When you save course delivery",
  readManagedMeetingRoomAvailability: "Read managed meeting room availability",
  providersOwnManagedMeetingRoomsManualLinksRemainValid:
    "Providers own managed meeting rooms. Room availability never changes your course delivery policy. Manual links remain valid.",
  roomCreationByProvider: "Room creation by provider",
  notEnabledForCourses: "Not enabled for courses",
  managedRoomCreationAvailable: "Managed room creation available",
  manualLinkOnly: "Manual link only",
  readCourseDeliveryChanges: "Read course delivery changes",
  noChangesRecordedYet: "No changes recorded yet.",
  courseDeliveryChangesNewestFirst: "Showing up to the latest 25 saves.",
  changesRecorded: "Changes recorded",
  savedWithNoChange: "Saved with no change.",
  thisChangeUsesAnOlderRecordFormat: "This change uses an older record format.",
  recorded: "Recorded",
  goBack: "Go back",
  preferredName: "Preferred name",
  yourInstructorsAndTheRegistrarCanSeeThisName:
    "Your instructors and the registrar can see this name.",
  phone: "Phone",
  theRegistrarCanSeeThisPhoneNumber: "The registrar can see this phone number.",
  address: "Address",
  theRegistrarCanSeeThisAddress: "The registrar can see this address.",
  aNewerValueIsWaitingChooseWhichToKeep:
    "A newer value is waiting. Choose which to keep.",
  saveNotConfirmedCheckBeforeSavingAgain:
    "Save not confirmed. Check before saving again.",
  thisValueWasNotSavedTryAgain: "This value was not saved. Try again.",
  saveNotConfirmedCheckAgain: "Save not confirmed. Check again.",
  noStudentRecordOnFileTheRegistrarCreatesIt:
    "No student record on file. The registrar creates it.",
  readYourOwnAcademicRecord: "Read your own academic record",
  theRegistrarMaintainsYourIdentityAndAcademicPlacement:
    "The registrar maintains your identity and academic placement.",
  studentID: "Student ID",
  honorific: "Honorific",
  legalName: "Legal name",
  program: "Program",
  cohort: "Cohort",
  status: "Status",
  email: "Email",
  city: "City",
  country: "Country",
  notRecorded: "Not recorded",
  maintainYourOwnContactDetails: "Maintain your own contact details",
  editEachContactDetailWhereItIsShown:
    "Edit each contact detail where it is shown.",
  yourContactDetails: "",
  thisFieldChangedSinceYouReadItYourTypedValueIsKept:
    "Compare with the newer value. Your typed value is kept.",
  check: "Check",
  studentAccount: "Student · Account",
  yourAcademicRecordAndTheContactDetailsYouMaintainYourself:
    "Your academic record and the contact details you maintain yourself.",
  someoneSavedANewerGradeScaleYourEditsAreKeptHere:
    "Another administrator saved a newer grade scale.",
  standing: "Letter grades",
  ratified: "Use for final grades",
  notYetRatified: "Advisory scale",
  chooseWhichGradeScaleToKeep: "Choose which grade scale to keep.",
  correctTheMarkedFieldsBeforeSaving:
    "Correct the marked fields before saving.",
  maintainTheInstitutionGradeScale: "Maintain the institution grade scale",
  theLetterToGPAScaleEverySectionSGradebookReadsForTheWholeInstitution:
    "The letter-to-GPA scale every section's gradebook reads, for the whole institution.",
  aNewerVersionIsWaiting: "A newer version is waiting",
  changedNotSaved: "Changed, not saved",
  institutionDecision: "Grade scale recorded",
  lettersAreNoLongerAdvisory: "Letters are no longer advisory.",
  lettersRemainAdvisoryUntilRatified: "Letters remain advisory until ratified.",
  gradeBands: "Grade bands",
  minimumScoreAndGPAPoints: "Minimum score and GPA points",
  letter: "Letter",
  minimumScore: "Minimum score",
  gPAPoints: "GPA points",
  goToField: "Go to field",
  saveScale: "Save scale",
  whenYouSaveTheGradeScale: "When you save the grade scale",
  everySectionUsesThisRatifiedScale: "Every section uses these grade bands for final grades.",
  everySectionUsesThisProvisionalScaleLettersStayAdvisory:
    "Every section uses this provisional scale; letters stay advisory.",
  gradeScaleSaved: "Grade scale saved.",
  recordOrClearTheRecordingSignInAttestation:
    "Record or clear the recording sign-in attestation",
  recordingSignIn: "Recording sign-in",
  attested: "Sign-in confirmed",
  recordingLinksStayNotPublishedUntilThisIsAttested:
    "Recording links stay unpublished until sign-in is confirmed.",
  notAttested: "Sign-in not confirmed",
  clear: "Clear",
  attestTheSetting: "Confirm sign-in",
  zoomSSetting: "Zoom's setting",
  iMAMCanTReadIt: "IMAM can't read it",
  clearOnlyIfTheRecordingSignInSettingInZoomWasUndone:
    "Clear only if the recording sign-in setting in Zoom was undone.",
  inZoomRequireSignInOnRecordingsBeforeAttestingHere:
    "In Zoom, require sign-in on recordings before confirming here.",
  clearAttestation: "Clear confirmation",
  whenYouClearTheAttestation: "When you clear the confirmation",
  whenYouAttestTheSetting: "When you confirm Zoom sign-in",
  publishedRecordingsStayPublishedFutureRecordingLinksWaitForAttestation:
    "Published recordings stay published. Future recording links wait for confirmation.",
  recordingLinksCanPublishAfterTheInstructorReviewsThem:
    "Recording links can publish after the instructor reviews them.",
  attestationClearedPublishedRecordingsStayPublished:
    "Confirmation cleared. Published recordings stay published.",
  recordingSignInAttestedRecordingLinksCanNowPublish:
    "Recording sign-in confirmed. Recording links can now publish.",
  notConfirmed: "Not confirmed",
  readTheResultOfThisSave: "Read the result of this save.",
  notSaved: "Not saved",
  thisSaveDidNotGoThroughReviewTheDraftAndSaveItWhenReady:
    "This save did not go through. Review the draft and save it when ready.",
  partlyDone: "Partly done",
  thisAdminReceivesEvents: "You receive recordings",
  anotherAdminReceivesEvents: "Another administrator receives recordings",
  receiverNeedsReplacement: "Choose a different administrator",
  receiverNotBound: "No administrator chosen",
  readZoomEventProcessingCapability: "Read Zoom event processing capability",
  zoomEventProcessing: "Zoom event processing",
  zoomOwnsRecordingEventsThisCapabilityNeverChangesCourseDeliveryPolicy:
    "Read-only here. IMAM processes recordings once an administrator is chosen to receive them.",
  bindTheCurrentAdministratorAsTheRecordingEventReceiver:
    "Bind the current administrator as the recording event receiver",
  zoomRecordingReceiver: "Receive recordings",
  bindTheSignedInIMAMAdministratorToAuthorizeRecordingEventProcessing:
    "Choose your signed-in administrator account to receive recordings from Zoom.",
  replace: "Replace",
  bindThisAdministrator: "Receive recordings here",
  receiver: "Administrator",
  thisAdministrator: "This administrator",
  thisUsesYourAuthenticatedIMAMIdentityZoomAccountDataNeverChoosesThisPerson:
    "This uses your signed-in IMAM account. Zoom never chooses the administrator.",
  replaceReceiverWithThisAdministrator:
    "Use this administrator instead",
  whenYouBindThisAdministrator: "When you choose this administrator",
  recordingEventsUseYourCurrentIMAMAdministratorAuthority:
    "Recordings are delivered to your current IMAM administrator account.",
  zoomRecordingReceiverUpdated: "Recording delivery updated.",
  retryFailedVerification: "Retry failed check",
  onlyTheVerificationThatFailedIsSentAgain:
    "Only the failed check is sent again.",
  readTheResultOfThisRefresh: "Read the result of this refresh.",
  notRefreshed: "Not refreshed",
  thisRefreshDidNotGoThroughCheckTheCurrentVerificationBeforeRefreshingAgain:
    "This refresh did not go through. Check current meeting access before refreshing again.",
  existingVerificationRead: "Zoom account read",
  verificationCouldNotBeRead: "Zoom account could not be read",
  noMatchingActiveBinding: "No current Zoom account",
  notReadYet: "Not read yet",
  refreshPartlyDone: "Refresh partly done",
  refreshRefused: "Refresh refused",
  refreshNotConfirmed: "Refresh not confirmed",
  zoomHostVerification: "Zoom meeting readiness",
  refreshObservationsForAnExistingImmutableZoomHostBinding:
    "Read and refresh an instructor's existing Zoom account",
  checkReadsTheCurrentConnectionAndHostBindingRefreshRecordsANewProviderObservation:
    "Check reads this instructor's current Zoom account. Refresh asks Zoom to confirm its meeting access.",
  instructor: "Instructor",
  existingIMAMIdentity: "Choose an instructor",
  chooseAnInstructor: "Choose an instructor",
  unnamedInstructor: "Unnamed instructor",
  checkExistingVerification: "Check Zoom access",
  readingVerification: "Checking Zoom access",
  connection: "Zoom account",
  enabled: "Enabled",
  disabled: "Disabled",
  verified: ". Verified",
  zoomHost: "Meeting access",
  license: "License:",
  noMatchingActiveConnectionAndHostBindingWereFoundNothingCanBeRefreshed:
    "This instructor has no current Zoom account to refresh.",
  verificationCouldNotBeReadCheckAgain:
    "The current Zoom account could not be read.",
  refreshZoomVerification: "Refresh Zoom access",
  whenYouRefreshVerification: "When you refresh verification",
  refreshTheExistingConnectionObservation:
    "Confirm that the current Zoom account is still available.",
  refreshThisInstructorSExistingHostObservation:
    "Confirm that this instructor can host Zoom meetings.",
  readAnExistingConnectionAndHostBindingBeforeRefreshing:
    "Choose an instructor and check their Zoom account first.",
} as const;

export const settingsText = {
  historyUnit: (count: number) => count === 1 ? "recent save" : count === 25 ? "most recent saves" : "recent saves",
  roomAvailability: (rooms: Record<"zoom" | "google_meet", boolean>) => `Zoom: ${rooms.zoom ? "managed rooms available" : "manual link only"}. Google Meet: ${rooms.google_meet ? "managed rooms available" : "manual link only"}.`,
  deliveryConflict: (parts: string[]) => {
    const labels = parts.map((part) => part === "methods" ? "meeting methods" : part === "default" ? "default meeting method" : "locations");
    const names = new Intl.ListFormat("en", { style: "long", type: "conjunction" }).format(labels);
    return names ? `Compare your ${names} with the newer values.` : "Compare with the newer settings.";
  },
  studentStatus: (status: string | null | undefined) =>
    (
      {
        active: "Active",
        leave_of_absence: "Leave of absence",
        completed: "Completed",
        withdrawn: "Withdrawn",
      } as Record<string, string>
    )[status ?? ""] ?? "Not recorded",
  zoomLicense: (status: "licensed" | "unlicensed" | "unknown") =>
    ({ licensed: "Licensed", unlicensed: "Unlicensed", unknown: "Not read" })[status],
  studentRecordAnswer: (sid: string | null) =>
    sid ? `Student ID ${sid}` : "Student record",
  enableMethod: (method: ConfigurableMeetingMethod) =>
    `Enable ${settingsCopy.method[method]}`,
  deliveryEffects: (before: DeliveryEntry | null, after: DeliveryEntry) => {
    if (!before) return [];
    const changes: Array<{ id: string; sentence: string }> = [];
    for (const method of METHODS) {
      if (after.enabled.includes(method) === before.enabled.includes(method))
        continue;
      changes.push({
        id: method,
        sentence: `${after.enabled.includes(method) ? "Enable" : "Disable"} ${LABEL[method]} for course delivery choices.`,
      });
    }
    if (before.defaultMethod !== after.defaultMethod)
      changes.push({
        id: "default",
        sentence: `New courses default to ${LABEL[after.defaultMethod]}. Courses already set up keep their method.`,
      });
    const locations = listDiff(before.locations, after.locations);
    if (locations.added.length)
      changes.push({
        id: "locations-added",
        sentence: `Add ${locations.added.join(", ")}.`,
      });
    if (locations.removed.length)
      changes.push({
        id: "locations-removed",
        sentence: `Remove ${locations.removed.join(", ")}.`,
      });
    return changes;
  },
  historyFacts: (at: string, own: boolean, reason: string | null) =>
    `${formatTimestamp(at)} · ${own ? "by you" : "by another administrator"}${reason ? ` · ${reason}` : ""}`,
  fieldsToFix: (count: number) =>
    `${count} field${count === 1 ? "" : "s"} to fix`,
  bandLabel: (label: string) => `Band ${label}`,
  bandFacts: (minimum: number, gpa: number) =>
    `Minimum score ${minimum}, GPA points ${gpa}`,
  uniqueBand: (position: number) => `Band ${position} needs a unique letter.`,
  bandMinimum: (label: string | number) =>
    `Minimum score for ${label} must be a whole number from 0 to 100.`,
  bandGpa: (label: string | number) =>
    `GPA points for ${label} must be between 0 and 4.`,
  attestation: (by: string | null | undefined, at: string | null | undefined) =>
    [by ? `Confirmed by ${by}` : "", at ? new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""].filter(Boolean).join(", "),
  eventProcessing: (configured: boolean, enabled: boolean) =>
    configured && enabled ? "Zoom is ready to send recordings to IMAM." : "Zoom is not ready to send recordings to IMAM.",
  verified: (at: string) => `Verified ${formatTimestamp(at)}`,
};
